import clsx from 'clsx';
import { FC, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Button } from 'react-bootstrap';
import { KTSVG, useDebounce } from '../../../../../_metronic/helpers';
import { deleteNote, useNotes } from '../../../../helpers/api';
import { NoteCard } from '../../../widgets/components/NoteCard';
import { Client, ClientProvider } from '../core/_models';
import { getClientById } from '../core/_requests';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../auth';
import { LoadingIndicator } from './LoadingIndicator';

type Props = {
  client?: Client;
  clientProvider?: ClientProvider;
  isLoading?: boolean;
};

const ClientNotes: FC<Props> = (props) => {
  const [addNew, setAddNew] = useState(false);
  const [addNewKey, setAddNewKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const { refetch, status: notesStatus, data: notes } = useNotes(props.client, debouncedSearchTerm, !!props.client);
  const { noteId } = useParams();
  const { currentProvider } = useAuth();

  useEffect(() => {
    if (props.client) refetch();
  }, [debouncedSearchTerm]);

  const defaultNewTitle = useMemo(() => moment().format('LL'), []);

  const handleAddNewCancel = async (orphanDraftUuid?: string) => {
    setAddNew(false);
    if (orphanDraftUuid && props.client) {
      try {
        await deleteNote(props.client, { uuid: orphanDraftUuid });
      } catch (err) {
        // best-effort cleanup — if delete fails, the draft will still appear in the list on next refetch.
      }
      refetch();
    }
    setAddNewKey((k) => k + 1);
  };

  const filteredNotes = noteId ? notes?.filter((note) => note.uuid === noteId) : notes;

  return (
    <>
      <div className="card shadow-sm mt-auto mb-5">
        <div className="card-header mobile-header">
          <h3 className="card-title">
            <div className={clsx({ 'd-flex align-items-center position-relative my-1': true })}>
              <KTSVG path="/media/icons/duotune/general/gen021.svg" className="svg-icon-1 position-absolute ms-3" />
              <input
                type="text"
                data-kt-client-table-filter="search"
                className={clsx('form-control form-control-solid ps-12 btn-sm' /*, { 'w-250px ': !compact }*/)}
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!props.client}
              />
              <button
                type="button"
                className="btn bg-transparent"
                style={{ marginLeft: '-50px', zIndex: '1' }}
                onClick={() => {
                  setSearchTerm('');
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </h3>
          {currentProvider && (
            <div className="card-toolbar">
              <Button
                className="btn btn-outline btn-outline-primary btn-sm me-3"
                onClick={() => {
                  setAddNewKey((k) => k + 1);
                  setAddNew(true);
                }}
              >
                Add New
              </Button>
            </div>
          )}
        </div>
      </div>
      {props.client && (
        <>
          {addNew && (
            <NoteCard
              key={`add-new-${addNewKey}`}
              client={props.client}
              clientProvider={props.clientProvider}
              defaultTitle={defaultNewTitle}
              allowLinkToSession={true}
              onCancel={handleAddNewCancel}
              onSave={() => {
                setAddNew(false);
                setAddNewKey((k) => k + 1);
                refetch();
              }}
            />
          )}
          {notesStatus === 'loading' && !notes ? (
            <div className="my-5">
              <LoadingIndicator />
            </div>
          ) : (
            filteredNotes &&
            filteredNotes.map((note) => (
              <NoteCard
                client={props.client!}
                clientProvider={props.clientProvider}
                note={note}
                key={note.uuid}
                allowLinkToSession={true}
                onUpdate={() => {
                  refetch();
                }}
              />
            ))
          )}
        </>
      )}
    </>
  );
};

export { ClientNotes };
